import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { analyticsAPI } from '../services/api';
import {
  DollarSign, Award, Users, Target, BarChart3
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Filler);

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

function formatPKR(amount) {
  const num = Number(amount) || 0;
  if (num >= 10000000) return `PKR ${(num / 10000000).toFixed(1).replace(/\.0$/, '')} Cr`;
  if (num >= 100000) return `PKR ${(num / 100000).toFixed(1).replace(/\.0$/, '')} Lac`;
  if (num >= 1000) return `PKR ${(num / 1000).toFixed(1).replace(/\.0$/, '')} K`;
  return formatCurrency(num);
}

function formatY(val) {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}Lac`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return `${val}`;
}

const stageColors = {
  new: '#1B4F8A',
  contacted: '#6C63C9',
  in_progress: '#1A7A5E',
  negotiating: '#C97D1A',
  won: '#4A7C1F',
  lost: '#C0392B',
};
const pipelineStages = ['new', 'contacted', 'in_progress', 'negotiating'];

function createCenterTextPlugin(total) {
  return {
    id: 'centerText',
    beforeDraw(chart) {
      const { width, height, ctx } = chart;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '500 26px Inter, sans-serif';
      ctx.fillStyle = '#1A1A1A';
      ctx.fillText(total.toString(), width / 2, height / 2 - 8);
      ctx.font = '11px Inter, sans-serif';
      ctx.fillStyle = '#888780';
      ctx.fillText('total leads', width / 2, height / 2 + 18);
      ctx.restore();
    },
  };
}

function getPeriodDateLabel(period) {
  const now = new Date();
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  switch (period) {
    case 'week': {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(now.getFullYear(), now.getMonth(), diff);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${fmt(start)} – ${fmt(end)}`;
    }
    case 'month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return `${fmt(start)} – ${fmt(end)}`;
    }
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1);
      const end = new Date(now.getFullYear(), q * 3 + 3, 0);
      return `${fmt(start)} – ${fmt(end)}`;
    }
    case 'year':
      return `${now.getFullYear()}`;
    default:
      return '';
  }
}

const avatarColors = ['#185FA5', '#0F6E56', '#854F0B', '#534AB7', '#A32D2D'];

const Analytics = () => {
  const [user, setUser] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const [kpi, setKpi] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [propertyPerf, setPropertyPerf] = useState([]);
  const [agentScore, setAgentScore] = useState([]);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const calls = [
        analyticsAPI.getKpiPeriod(period),
        analyticsAPI.getRevenueTrend(period),
        analyticsAPI.getConversionFunnel(period),
        analyticsAPI.getSources(),
        analyticsAPI.getPropertyPerformance(period),
        u.role === 'admin' ? analyticsAPI.getAgentScorePeriod(period) : Promise.resolve({ data: [] }),
      ];
      const results = await Promise.allSettled(calls);
      const d = (r) => (r.status === 'fulfilled' ? r.value.data : null);
      const dArr = (r) => (r.status === 'fulfilled' && Array.isArray(r.value.data) ? r.value.data : []);
      setKpi(d(results[0]));
      setRevenueTrend(dArr(results[1]));
      setFunnel(dArr(results[2]) || []);
      setLeadSources(dArr(results[3]));
      setPropertyPerf(dArr(results[4]));
      setAgentScore(dArr(results[5]));
    } catch (err) {
      console.error('Analytics load error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const funnelData = useMemo(() => {
    if (!funnel.length) return [];
    const order = { new: 1, contacted: 2, viewing: 3, in_progress: 3, follow_up: 3, negotiating: 4, won: 5, lost: 6 };
    const sorted = [...funnel].sort((a, b) => (order[a.stage] || 99) - (order[b.stage] || 99));
    const top = sorted.length > 0 ? Math.max(...sorted.map(s => Number(s.count))) : 0;
    const total = sorted.reduce((s, x) => s + Number(x.count), 0);
    const won = sorted.find(s => s.stage === 'won');
    const lost = sorted.find(s => s.stage === 'lost');
    const pipeline = sorted.filter(s => pipelineStages.includes(s.stage));
    const overallRate = total > 0 && won ? Math.round((Number(won.count) / total) * 1000) / 10 : 0;
    const activeInPipeline = pipeline.reduce((s, x) => s + Number(x.count), 0);
    return sorted.map((s, i) => {
      const count = Number(s.count);
      const prevCount = i > 0 ? Number(sorted[i - 1].count) : 0;
      const dropOff = i > 0 && prevCount > 0 ? Math.round((1 - count / prevCount) * 100) : 0;
      const pct = top > 0 ? Math.round((count / top) * 100) : 0;
      return { ...s, count, dropOff, pct, overallRate, activeInPipeline, total, won, lost };
    });
  }, [funnel]);

  const hasRevenueData = useMemo(() => {
    return revenueTrend.some(r => (r.current || 0) > 0) || revenueTrend.some(r => (r.previous || 0) > 0);
  }, [revenueTrend]);

  const chartData = useMemo(() => {
    if (!revenueTrend.length || !hasRevenueData) return null;
    return {
      labels: revenueTrend.map(r => r.label),
      datasets: [
        {
          label: 'Current Period',
          data: revenueTrend.map(r => r.current || 0),
          borderColor: '#378ADD',
          backgroundColor: 'rgba(55,138,221,0.10)',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: '#378ADD',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Previous Period',
          data: revenueTrend.map(r => r.previous || 0),
          borderColor: '#B4B2A9',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 2,
          pointBackgroundColor: '#B4B2A9',
          tension: 0.4,
          fill: false,
        },
      ],
    };
  }, [revenueTrend, hasRevenueData]);

  const chartMax = useMemo(() => {
    if (!revenueTrend.length) return 0;
    const allVals = revenueTrend.flatMap(r => [r.current || 0, r.previous || 0]);
    return Math.max(...allVals, 0);
  }, [revenueTrend]);

  const revenueLineOptions = useMemo(() => {
    const paddedMax = chartMax * 1.1 || 1;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          backgroundColor: '#fff',
          titleColor: '#1A1A1A',
          bodyColor: '#888780',
          borderColor: 'rgba(0,0,0,0.1)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          titleFont: { size: 12, weight: '500' },
          bodyFont: { size: 12 },
        },
        legend: { display: false },
      },
      scales: {
        x: {
          grid: { color: 'rgba(136,135,128,0.15)', drawBorder: false },
          ticks: { font: { size: 10 }, color: '#888780', autoSkip: false, maxRotation: 0 },
        },
        y: {
          grid: { color: 'rgba(136,135,128,0.15)', drawBorder: false },
          ticks: { font: { size: 10 }, color: '#888780', callback: (v) => formatY(v) },
          beginAtZero: true,
          min: 0,
          max: paddedMax,
        },
      },
    };
  }, [chartMax]);


  const periodDateLabel = getPeriodDateLabel(period);

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto' }}>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} } .ap { animation: pulse 1.5s ease-in-out infinite; }`}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div className="ap" style={{ width: '200px', height: '22px', background: '#F1F0EC', borderRadius: '4px', marginBottom: '8px' }} />
            <div className="ap" style={{ width: '280px', height: '13px', background: '#F1F0EC', borderRadius: '4px' }} />
          </div>
          <div className="ap" style={{ width: '120px', height: '32px', background: '#F1F0EC', borderRadius: '20px' }} />
        </div>
      <div className="analytics-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
          {[1,2,3,4].map(i => <div key={i} className="ap" style={{ height: '96px', background: '#F1F0EC', borderRadius: '8px' }} />)}
        </div>
        <div className="ap" style={{ height: '280px', background: '#F1F0EC', borderRadius: '12px', marginBottom: '16px' }} />
        <div className="analytics-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div className="ap" style={{ height: '240px', background: '#F1F0EC', borderRadius: '12px' }} />
          <div className="ap" style={{ height: '240px', background: '#F1F0EC', borderRadius: '12px' }} />
        </div>
      </div>
    );
  }

  const sourceValues = leadSources.map(s => Number(s.count));
  const yMax = sourceValues.length > 0
    ? Math.ceil(Math.max(...sourceValues) * 1.2 / 10) * 10
    : 10;

  return (
    <div className="analytics-wrap" style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto' }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .a-card { background: #FFFFFF; border: 1px solid rgba(0,0,0,0.08); border-radius: 12px; overflow: hidden; }
        .a-title { font-size: 14px; font-weight: 500; color: #1A1A1A; }
        .a-divider { height: 0.5px; background: rgba(0,0,0,0.08); margin: 0; }
        .a-row:hover { background: #F8F8F6; }
        @media (max-width: 1023px) { .analytics-wrap { padding: 16px !important; } .analytics-wrap .a-card-header { padding: 14px 16px !important; } .analytics-wrap .a-padded { padding-left: 16px !important; padding-right: 16px !important; } }
        @media (max-width: 767px) { .analytics-kpi { grid-template-columns: repeat(2, 1fr) !important; } .analytics-two-col { grid-template-columns: 1fr !important; } .analytics-funnel-inner { grid-template-columns: 1fr !important; gap: 12px !important; } .analytics-funnel-donut { width: 140px !important; height: 140px !important; margin: 0 auto !important; } .analytics-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; } .analytics-kpi-val { font-size: 20px !important; overflow-wrap: break-word !important; } }
        @media (max-width: 480px) { .analytics-wrap { padding: 12px !important; } .analytics-kpi { grid-template-columns: 1fr !important; } .analytics-kpi-val { font-size: 22px !important; } .analytics-period { width: 100% !important; justify-content: center !important; } .analytics-funnel-stats { grid-template-columns: 1fr !important; gap: 8px !important; } .analytics-funnel-stats > div { padding: 14px 16px !important; } .analytics-funnel-stats > div > div:first-child { font-size: 20px !important; } }
      `}</style>

      <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 500, color: '#1A1A1A', margin: 0 }}>Analytics & Reports</h1>
          <p style={{ fontSize: '13px', color: '#888780', marginTop: '2px' }}>{periodDateLabel}</p>
        </div>
        <div className="analytics-period" style={{ display: 'flex', gap: '4px', background: '#fff', borderRadius: '20px', border: '0.5px solid rgba(0,0,0,0.08)', padding: '3px' }}>
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              style={{
                padding: '4px 14px', fontSize: '12px', fontWeight: 500, borderRadius: '20px', border: 'none',
                cursor: 'pointer', transition: 'all 0.15s',
                background: period === p.value ? '#E6F1FB' : 'transparent',
                color: period === p.value ? '#185FA5' : '#888780',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {[
          { label: 'Total Revenue', value: kpi?.revenue?.current, variance: kpi?.revenue?.variance, prevValue: kpi?.revenue?.previous, icon: DollarSign, fmt: formatPKR, borderColor: '#639922' },
          { label: 'Deals Closed', value: kpi?.dealsClosed?.current, variance: kpi?.dealsClosed?.variance, prevValue: kpi?.dealsClosed?.previous, icon: Award, borderColor: '#378ADD' },
          { label: 'New Leads', value: kpi?.newLeads?.current, variance: kpi?.newLeads?.variance, prevValue: kpi?.newLeads?.previous, icon: Users, borderColor: '#534AB7' },
          { label: 'Conversion Rate', value: kpi?.conversionRate?.current, variance: kpi?.conversionRate?.variance, prevValue: kpi?.conversionRate?.previous, icon: Target, suffix: '%', borderColor: '#EF9F27' },
        ].map((card, idx) => {
          const isPos = (card.variance || 0) > 0;
          const varText = card.variance === 0 || card.variance === null
            ? (card.prevValue !== undefined && card.prevValue === 0 ? 'No previous data' : '')
            : `${isPos ? '↑' : '↓'} ${Math.abs(card.variance)}%`;
          const hasVariance = card.variance !== 0 && card.variance != null;
          const displayVal = card.fmt ? card.fmt(card.value) : (card.value ?? 0);
          return (
            <div key={idx} style={{ background: '#F1F0EC', borderRadius: '8px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: `4px solid ${card.borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <card.icon size={16} color="#888780" />
                <span style={{ fontSize: '12px', fontWeight: 400, color: '#888780' }}>{card.label}</span>
              </div>
              <div className="analytics-kpi-val" style={{ fontSize: '28px', fontWeight: 500, color: '#1A1A1A', lineHeight: 1.15 }}>
                {displayVal}{card.suffix || ''}
              </div>
              <div style={{ fontSize: '11px', color: isPos ? '#3B6D11' : (card.variance || 0) < 0 ? '#A32D2D' : '#B4B2A9' }}>
                {hasVariance ? varText : (varText || <span style={{ color: '#B4B2A9' }}>No previous data</span>)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Revenue Trend */}
      <div className="a-card" style={{ marginBottom: '12px' }}>
        <div className="a-card-header" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="a-title">Revenue Trend</span>
          {chartData && (
            <div style={{ display: 'flex', gap: '16px', fontSize: '10px', color: '#888780' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#378ADD', display: 'inline-block' }} />
                Current Period
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '14px', height: '2px', borderTop: '2px dashed #B4B2A9', display: 'inline-block' }} />
                Previous Period
              </span>
            </div>
          )}
        </div>
        <div className="a-divider" />
        <div className="a-padded" style={{ padding: '16px 24px' }}>
          {!chartData ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
              <BarChart3 size={32} color="#B4B2A9" />
              <p style={{ fontSize: '13px', color: '#888780', marginTop: '8px' }}>
                {revenueTrend.length > 0 ? 'No revenue recorded for this period' : 'No revenue data'}
              </p>
            </div>
          ) : (
            <div style={{ height: '280px' }}>
              <Line data={chartData} options={revenueLineOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Conversion Funnel + Lead Sources */}
      <div className="analytics-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div className="a-card">
        <div className="a-card-header" style={{ padding: '20px 24px' }}>
          <span className="a-title">Lead Conversion Funnel</span>
        </div>
          <div className="a-divider" />
          {funnelData.length === 0 ? (
            <div style={{ padding: '20px 24px', fontSize: '13px', color: '#888780' }}>No funnel data</div>
          ) : (
            <div className="analytics-funnel-inner" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', alignItems: 'center', padding: '20px 24px' }}>
              {/* Left: Donut */}
              <div className="analytics-funnel-donut" style={{ width: '200px', height: '200px' }}>
                <Doughnut
                  data={{
                    labels: funnelData.map(s => s.stage === 'in_progress' ? 'In Progress' : s.stage?.replace(/_/g, ' ')),
                    datasets: [{
                      data: funnelData.map(s => s.count),
                      backgroundColor: funnelData.map(s => stageColors[s.stage] || '#888780'),
                      borderWidth: 0,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '70%',
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#1A1A1A',
                        bodyColor: '#888780',
                        borderColor: 'rgba(0,0,0,0.1)',
                        borderWidth: 1,
                        cornerRadius: 8,
                        padding: 10,
                        callbacks: {
                          label: (ctx) => `${ctx.parsed} leads (${Math.round((ctx.parsed / funnelData.reduce((s, x) => s + x.count, 0)) * 100)}%)`,
                        },
                      },
                    },
                  }}
                  plugins={[createCenterTextPlugin(funnelData.reduce((s, x) => s + x.count, 0))]}
                />
              </div>
              {/* Right: Pipeline flow + won/lost + stats */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 500, color: '#B4B2A9', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Pipeline Flow</div>
                {pipelineStages.map(stageKey => {
                  const stage = funnelData.find(s => s.stage === stageKey);
                  if (!stage) return null;
                  const maxCount = Math.max(...funnelData.map(s => s.count), 1);
                  const barPct = (stage.count / maxCount) * 100;
                  const color = stageColors[stageKey];
                  const stageLabel = stageKey === 'in_progress' ? 'In Progress' : stageKey.charAt(0).toUpperCase() + stageKey.slice(1);
                  return (
                    <div key={stageKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1A1A1A', minWidth: '80px' }}>{stageLabel}</span>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <div style={{ height: '6px', width: `${Math.max(barPct, 1)}%`, borderRadius: '3px', background: color, transition: 'width 0.5s ease', minWidth: '4px' }} />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1A1A1A', minWidth: '24px', textAlign: 'right' }}>{stage.count}</span>
                      {stage.dropOff > 0 && (
                        <span style={{ fontSize: '10px', fontWeight: 500, padding: '1px 6px', borderRadius: '8px', background: '#FCEBEB', color: '#C0392B', whiteSpace: 'nowrap' }}>
                          -{stage.dropOff}%
                        </span>
                      )}
                    </div>
                  );
                })}
                <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.08)', margin: '8px 0' }} />
                {/* Won / Lost inline pills */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  {funnelData.filter(s => s.stage === 'won' || s.stage === 'lost').map(s => (
                    <span key={s.stage} style={{ fontSize: '12px', fontWeight: 500, color: '#1A1A1A' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: stageColors[s.stage], display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }} />
                      {s.stage.charAt(0).toUpperCase() + s.stage.slice(1)} {s.count}
                    </span>
                  ))}
                </div>
                {/* Bottom stats */}
                <div className="analytics-funnel-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                  {[
                    { label: 'Conversion Rate', value: funnelData[0]?.overallRate != null ? `${funnelData[0].overallRate}%` : '0%', bg: '#EAF3DE', color: '#3B6D11' },
                    { label: 'Lost', value: (funnelData.find(s => s.stage === 'lost')?.count ?? 0), bg: '#FCEBEB', color: '#C0392B' },
                    { label: 'Active in Pipeline', value: funnelData[0]?.activeInPipeline ?? 0, bg: '#E6F1FB', color: '#185FA5' },
                  ].map((stat, i) => (
                    <div key={i} style={{ background: stat.bg, borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: stat.color }}>{stat.value}</div>
                      <div style={{ fontSize: '10px', color: '#888780', marginTop: '2px' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="a-card">
        <div className="a-card-header" style={{ padding: '20px 24px' }}>
          <span className="a-title">Lead Sources</span>
        </div>
          <div className="a-divider" />
          {leadSources.length === 0 ? (
            <div style={{ padding: '20px 24px', fontSize: '13px', color: '#888780' }}>No lead source data</div>
          ) : (
            <div className="a-padded" style={{ padding: '20px 24px' }}>
              <div style={{ height: '240px' }}>
                <Bar
                  data={{
                    labels: leadSources.map(s =>
                      s.source.length > 10 ? s.source.slice(0, 10) + '...' : s.source
                    ),
                    datasets: [{
                      label: 'Leads',
                      data: sourceValues,
                      backgroundColor: '#185FA5',
                      borderRadius: 4,
                      borderSkipped: 'bottom',
                      barThickness: 28,
                      categoryPercentage: 0.6,
                      hoverBackgroundColor: '#E6F1FB',
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                      padding: { top: 8, left: 16, right: 16, bottom: 0 },
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        enabled: true,
                        backgroundColor: '#fff',
                        titleColor: '#888780',
                        titleFont: { size: 11 },
                        bodyColor: '#1A1A1A',
                        bodyFont: { size: 14, weight: '500' },
                        borderColor: 'rgba(0,0,0,0.08)',
                        borderWidth: 1,
                        cornerRadius: 6,
                        padding: { top: 8, bottom: 8, left: 12, right: 12 },
                        callbacks: {
                          title: (items) => items[0].label,
                          label: (ctx) => `${ctx.parsed} leads`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { display: false, drawBorder: false },
                        ticks: { font: { size: 11 }, color: '#888780' },
                      },
                      y: {
                        grid: { color: '#e5e7eb', drawBorder: false },
                        ticks: { font: { size: 11 }, color: '#888780', precision: 0, count: 5 },
                        beginAtZero: true,
                        max: yMax,
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {user?.role === 'admin' && (
        <div className="a-card" style={{ marginBottom: '12px' }}>
          <div className="a-card-header" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="a-title">Agent Performance Score</span>
          </div>
          <div className="a-divider" />
          {agentScore.length === 0 ? (
            <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#888780' }}>No agent data for this period</span>
            </div>
          ) : (
            agentScore.map((agent, idx) => {
              const gradeColor = agent.grade === 'A' ? '#3B6D11' : agent.grade === 'B' ? '#854F0B' : '#A32D2D';
              const gradeBg = agent.grade === 'A' ? '#EAF3DE' : agent.grade === 'B' ? '#FAEEDA' : '#FCEBEB';
              const barColor = agent.score >= 75 ? '#3B6D11' : agent.score >= 50 ? '#EF9F27' : '#A32D2D';
              return (
                <div key={agent.id} style={{ padding: '12px 24px', borderBottom: idx < agentScore.length - 1 ? '0.5px solid rgba(0,0,0,0.08)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: '#B4B2A9', minWidth: '20px' }}>#{idx + 1}</span>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: avatarColors[idx % 5], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>{(agent.full_name || agent.username || '?').charAt(0).toUpperCase()}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{agent.full_name || agent.username}</span>
                        <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 8px', borderRadius: '10px', background: gradeBg, color: gradeColor }}>{agent.grade}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                        <div style={{ flex: 1, height: '8px', borderRadius: '4px', background: '#F1F0EC', overflow: 'hidden', maxWidth: '200px' }}>
                          <div style={{ height: '100%', width: `${agent.score}%`, borderRadius: '4px', background: barColor, transition: 'width 0.5s ease' }} />
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: 700, color: barColor }}>{agent.score}<span style={{ fontSize: '11px', fontWeight: 400, color: '#B4B2A9' }}>/100</span></span>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '10px', color: '#888780' }}>
                        <span>Updates {agent.statusUpdates}</span>
                        <span>Comms {agent.comms}</span>
                        <span>Tasks {agent.tasksOnTime}</span>
                        <span>Deals {agent.dealsWon}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="a-card">
        <div className="a-card-header" style={{ padding: '20px 24px' }}>
          <span className="a-title">Property Performance</span>
        </div>
        <div className="a-divider" />
        {propertyPerf.length === 0 ? (
          <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#888780' }}>No property data</span>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 400, color: '#B4B2A9', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>Property</th>
                  <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 400, color: '#B4B2A9', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>Location</th>
                  <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 400, color: '#B4B2A9', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>Price</th>
                  <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 400, color: '#B4B2A9', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>Inquiries</th>
                  <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 400, color: '#B4B2A9', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {propertyPerf.map((prop, idx) => {
                  const bs = (() => {
                    const s = (prop.status || '').toLowerCase();
                    const map = { available: { bg: '#EAF3DE', color: '#3B6D11' }, sold: { bg: '#FCEBEB', color: '#A32D2D' }, reserved: { bg: '#FAEEDA', color: '#854F0B' } };
                    return map[s] || { bg: '#F1F0EC', color: '#888780' };
                  })();
                  return (
                    <tr key={prop.id} style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8F8F6'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '10px 16px', fontWeight: 500, color: '#1A1A1A', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>{prop.title || `Property #${prop.id}`}</td>
                      <td style={{ padding: '10px 16px', color: '#888780', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>{prop.location || '-'}</td>
                      <td style={{ padding: '10px 16px', color: '#1A1A1A', textAlign: 'right', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>{formatPKR(prop.price)}</td>
                      <td style={{ padding: '10px 16px', color: '#1A1A1A', textAlign: 'center', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>{prop.inquiry_count || 0}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
                        <span style={{ fontSize: '10px', fontWeight: 500, padding: '2px 8px', borderRadius: '20px', background: bs.bg, color: bs.color, whiteSpace: 'nowrap' }}>
                          {prop.status || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
